import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment, createElementVNode as _createElementVNode, unref as _unref, vModelText as _vModelText, withDirectives as _withDirectives, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createSlots as _createSlots, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-27e8c34a"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "alert alert-warning alert-sm"
}
const _hoisted_2 = { class: "row" }
const _hoisted_3 = { class: "col-sm-6" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", {
  for: "givenNameInput",
  class: "form-label"
}, "Given name", -1))
const _hoisted_5 = ["readonly"]
const _hoisted_6 = { class: "col-sm-6" }
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", {
  for: "familyNameInput",
  class: "form-label"
}, "Surname", -1))
const _hoisted_8 = ["readonly"]
const _hoisted_9 = { class: "row" }
const _hoisted_10 = { class: "col-sm-8" }
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", {
  for: "addressInput",
  class: "form-label"
}, "Email address", -1))
const _hoisted_12 = ["readonly"]

import { ref, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { getSafeRedirectUri, usePageTitle } from '@webapp/util/AppUtil'
import { useIdentityBlockGrantStore } from './IdentityBlockGrantStore'
import { useSignInStore } from '@webapp/auth/SignInStore'
import { useIdentityApi, SignUpRequest, BlockGrant } from '@webapp/auth/IdentityApi'
import { AddressUtil } from '@webapp/util/AddressUtil'
import { Environment } from '@webapp/util/Environment'
import TaskPage from '@webapp/util/TaskPage.vue'
import LoadingIndicator from '@/util/LoadingIndicator.vue'
import VueTurnstile from 'vue-turnstile'
import { pluralizeWithCount } from '@/util/LanguageUtil'


export default /*#__PURE__*/_defineComponent({
  __name: 'SignUpPage',
  setup(__props) {

usePageTitle("Sign up")

const loadingRef = ref(true)
const signInRestrictedRef = ref(false)
const captchaCodeRef = ref("")
// Turnstile must render after a nextTick delay so its container element exists in the DOM.
// Without this, Cloudflare's script fires before Vue flushes the v-if block, causing
// "Invalid type for parameter container" errors that leave the captcha (and sign-up button) broken.
const turnstileReadyRef = ref(false)

const identityApi = useIdentityApi()
const signInStore = useSignInStore()
const router = useRouter()

const showPreviewAlertRef = computed(() => signInRestrictedRef.value && !signInStore.unusedGrant)
const canEditRef = computed(() => !busyRef.value)

const canSignUpRef = computed(() => !busyRef.value && !!(signInStore.givenName || signInStore.familyName) && AddressUtil.isValidEmail(signInStore.address) && !!(isDevEnv || captchaCodeRef.value))
const busyRef = ref(false)
const isDevEnv = Environment.isDevelopment()
const captchaAppearanceRef = computed(() => isDevEnv && router.currentRoute.value.query.forceCaptcha ? "always" : "interaction-only")
const tsKey = Environment.get('TURNSTILE_KEY')

const givenNameInputRef = ref<HTMLInputElement>()
const addressInputRef = ref<HTMLInputElement>()

async function signUp() {
  if (signInStore.subject && signInRestrictedRef.value) {
    // already signed up but sign in not allowed yet
    router.push('/signup/done')
  }
  else {
    try {
      busyRef.value = true

      const request = {
        givenName: signInStore.givenName,
        familyName: signInStore.familyName,
        address: signInStore.address,
        captchaCode: captchaCodeRef.value,
      } as SignUpRequest

      const newIdent = await identityApi.signUpAsync(request)
      signInStore.subject = newIdent.subject ?? ""
      // store this so the next page knows to come back here if requested
      signInStore.isNewUser = true

      await identityApi.sendSignInCodeAsync(signInStore.address)
      router.push({ path: '/signin/code', query: { redirect_uri: getSafeRedirectUri() } })
    }
    catch (error) {
      if (signInRestrictedRef.value) {
        router.push('/signup/done')
      }
    }
    finally {
      busyRef.value = false
    }
  }
}

onMounted(async () => {
  try {
    loadingRef.value = true
    signInRestrictedRef.value = await identityApi.isSignInRestrictedAsync(signInStore.subject || undefined)
  }
  finally {
    loadingRef.value = false
  }

  await nextTick()
  turnstileReadyRef.value = true

  if (signInStore.givenName) {
    addressInputRef.value!.focus()
    addressInputRef.value!.select()
  }
  else {
    givenNameInputRef.value!.focus()
  }
})


return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(TaskPage, {
    class: "signup-identity-page",
    "no-back": "",
    "can-continue": canSignUpRef.value,
    onAction: signUp
  }, _createSlots({
    title: _withCtx(() => [
      _createTextVNode("Get started")
    ]),
    instruction: _withCtx(() => [
      (!loadingRef.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createTextVNode(" Please tell us your name and email address. ")
          ], 64))
        : _createCommentVNode("", true)
    ]),
    default: _withCtx(() => [
      (loadingRef.value)
        ? (_openBlock(), _createBlock(LoadingIndicator, { key: 0 }))
        : _createCommentVNode("", true),
      (!loadingRef.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _hoisted_4,
                _withDirectives(_createElementVNode("input", {
                  type: "text",
                  class: "form-control",
                  id: "givenNameInput",
                  ref_key: "givenNameInputRef",
                  ref: givenNameInputRef,
                  readonly: !canEditRef.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(signInStore).givenName) = $event))
                }, null, 8, _hoisted_5), [
                  [_vModelText, _unref(signInStore).givenName]
                ])
              ]),
              _createElementVNode("div", _hoisted_6, [
                _hoisted_7,
                _withDirectives(_createElementVNode("input", {
                  type: "text",
                  class: "form-control",
                  id: "familyNameInput",
                  readonly: !canEditRef.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(signInStore).familyName) = $event))
                }, null, 8, _hoisted_8), [
                  [_vModelText, _unref(signInStore).familyName]
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("div", _hoisted_10, [
                _hoisted_11,
                _withDirectives(_createElementVNode("input", {
                  type: "text",
                  class: "form-control",
                  id: "addressInput",
                  ref_key: "addressInputRef",
                  ref: addressInputRef,
                  readonly: !canEditRef.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(signInStore).address) = $event))
                }, null, 8, _hoisted_12), [
                  [_vModelText, _unref(signInStore).address]
                ])
              ])
            ]),
            (turnstileReadyRef.value)
              ? (_openBlock(), _createBlock(_unref(VueTurnstile), {
                  key: 0,
                  "site-key": _unref(tsKey),
                  appearance: captchaAppearanceRef.value,
                  theme: "light",
                  modelValue: captchaCodeRef.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((captchaCodeRef).value = $event)),
                  style: {"display":"flex"}
                }, null, 8, ["site-key", "appearance", "modelValue"]))
              : _createCommentVNode("", true),
            _createVNode(_component_router_link, {
              class: "fs-small",
              to: "/signin"
            }, {
              default: _withCtx(() => [
                _createTextVNode("I already have an account")
              ]),
              _: 1
            })
          ], 64))
        : _createCommentVNode("", true)
    ]),
    _: 2
  }, [
    (!loadingRef.value)
      ? {
          name: "alert",
          fn: _withCtx(() => [
            (showPreviewAlertRef.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_1, " We're in preview, and the preview is full at the moment. Sign up now, and we'll save your place in line. We're adding new people each week! "))
              : _createCommentVNode("", true)
          ]),
          key: "0"
        }
      : undefined
  ]), 1032, ["can-continue"]))
}
}

})